/**
 * How a booking is paid.
 *
 * Almost everything we sell is pay-on-arrival: the guest reserves through us
 * and the provider takes the money on the day. Stays are not: owner ruling
 * 2026-09-25, every stay is paid online. The desert camps, which live in the
 * activities catalogue, are paid in full from a secure payment link our team
 * emails after the reservation.
 *
 * Hotels, riads and villas are the other half of that rule and are not
 * represented here: they run through LiteAPI, which charges the guest at
 * booking time and sets its own cancellation terms.
 */

/**
 * Activities paid by an emailed payment link rather than on arrival.
 *
 * Deliberately a list of slugs, not a rule derived from pricing or category.
 * The previous behaviour inferred it ("this booking has nights, so it must
 * take a deposit"), and that inference broke the moment villas moved to
 * LiteAPI: the two camps left behind kept receiving deposit copy for a deposit
 * that no longer existed. Two camps named explicitly cannot drift; an
 * inference over a changing catalogue already did.
 *
 * Adding a third camp means editing this list and shipping. That is the
 * intended cost: it puts a human in front of a change to what a guest is told
 * about money.
 */
export const PAYMENT_LINK_SLUGS: readonly string[] = ["alkamar-camp", "le-bedouin-camp"];

export type PaymentMode = "link" | "arrival";

/** True when this activity is paid in full from a link we email. */
export function paysByLink(slug: string | null | undefined): boolean {
  if (!slug) return false;
  return PAYMENT_LINK_SLUGS.includes(slug.trim().toLowerCase());
}

/**
 * Providers that take cash only on the day: no card machine. Named per slug
 * (Marajah Spa) and per category (every quad, buggy and horse ride), owner
 * ruling 2026-10-13, for the same reason as `PAYMENT_LINK_SLUGS`. The
 * `cashOnlyOnArrival` column is not trusted for guest copy: stale flags on it
 * once told card-only guests the wrong thing.
 */
export const CASH_ONLY_SLUGS: readonly string[] = ["marajah-spa"];
export const CASH_ONLY_CATEGORY_SLUGS: readonly string[] = ["quad", "buggy", "horse"];

/** True when the guest must bring cash: no card is taken on the day. */
export function paysCashOnly(
  slug: string | null | undefined,
  categorySlug: string | null | undefined,
): boolean {
  if (paysByLink(slug)) return false;
  if (slug && CASH_ONLY_SLUGS.includes(slug.trim().toLowerCase())) return true;
  return !!categorySlug && CASH_ONLY_CATEGORY_SLUGS.includes(categorySlug.trim().toLowerCase());
}

/** The payment mode for an activity, for copy that has to name one. */
export function paymentModeFor(slug: string | null | undefined): PaymentMode {
  return paysByLink(slug) ? "link" : "arrival";
}

/**
 * Venues that take a part payment up front for larger groups, by a link our
 * team emails, with the rest paid on arrival.
 *
 * Les Bains du Lotus (Marrakech spa) asks 50% for more than 4 guests; owner
 * ruling 2026-10-10. Named per slug for the same reason as
 * `PAYMENT_LINK_SLUGS`: a change to what a guest is told about money should go
 * through a person, not an inference.
 *
 * Copy names the percentage, never an amount: the link is for a share of the
 * total the guest already sees, so restating it in a second currency-rounded
 * figure would only create a number that can disagree with that total.
 */
export const GROUP_DEPOSIT_RULES: Readonly<
  Record<string, { readonly overGuests: number; readonly percent: number }>
> = {
  "les-bains-du-lotus": { overGuests: 4, percent: 50 },
};

export type GroupDeposit = { overGuests: number; percent: number };

/**
 * The group deposit this party owes, or null when the venue has no rule or the
 * party is within its limit. `guests` counts everyone booked (adults,
 * children and infants alike): the spa counts heads, not price bands.
 */
export function groupDepositFor(
  slug: string | null | undefined,
  guests: number,
): GroupDeposit | null {
  if (!slug) return null;
  const rule = GROUP_DEPOSIT_RULES[slug.trim().toLowerCase()];
  if (!rule || !(guests > rule.overGuests)) return null;
  return { overGuests: rule.overGuests, percent: rule.percent };
}
